import React, { useState, useMemo } from "react";
import { ScrollView, View, TouchableOpacity, StyleSheet, TextInput } from "react-native";
import NSText from "./NSText";
import { AntDesign } from "@expo/vector-icons";
import { Feather } from "@expo/vector-icons";

export interface NestedDataType {
  id: string;
  name: string;
  isDisable: boolean;
  children:
    | {
        id: string;
        name: string;
        children: { id: string; name: string; isDisable: boolean; }[] | null;
        isDisable: boolean;
      }[]
    | null;
}

const shadowStyle = {
  marginBottom: 2,
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.25,
  shadowRadius: 3.84,
  elevation: 5,
};

const GlobalNestedPicker = ({
  width,
  coords,
  data,
  selectedId,
  onItemPress,
  setRuntimeHeight,
  closeDropDown,
  hasSearch = false,
  hasDeepSearch = false,
  searchPlaceholder
}: {
  width: number;
  coords: { x: number; y: number };
  data: NestedDataType[];
  selectedId?: string;
  onItemPress: (value: any, index: number) => void;
  setRuntimeHeight: (value: number)=>void;
  closeDropDown: ()=>void;
  hasSearch?: boolean;
  hasDeepSearch?: boolean;
  searchPlaceholder?: string;
}) => {
  const [openedIndex, setOpenedIndex] = useState<number>(-1);
  const [openedSubIndex, setOpenedSubIndex] = useState<number>(-1);
  const [searchText, setSearchText] = useState<string>("");

  // Shallow search: only matches top-level item names
  const filterShallow = (data: NestedDataType[], term: string): NestedDataType[] => {
    return data.filter(item => item.name.toLowerCase().includes(term));
  };

  // Deep search: matches across all nested levels (parent → child → grandchild)
  // Only keeps matching branches — filtered children under their parent
  const filterDeep = (data: NestedDataType[], term: string): NestedDataType[] => {
    return data.reduce<NestedDataType[]>((acc, item) => {
      const parentMatches = item.name.toLowerCase().includes(term);

      // If parent matches, include entire item as-is
      if (parentMatches) {
        acc.push(item);
        return acc;
      }

      if (item.children) {
        const filteredChildren = item.children.reduce<NonNullable<NestedDataType["children"]>>((childAcc, child) => {
          const childMatches = child.name.toLowerCase().includes(term);

          // If child matches, include it with all its grandchildren
          if (childMatches) {
            childAcc.push(child);
            return childAcc;
          }

          // Check grandchildren
          if (child.children) {
            const filteredGrandChildren = child.children.filter(grandChild =>
              grandChild.name.toLowerCase().includes(term)
            );
            if (filteredGrandChildren.length > 0) {
              childAcc.push({ ...child, children: filteredGrandChildren });
              return childAcc;
            }
          }

          return childAcc;
        }, []);

        if (filteredChildren.length > 0) {
          acc.push({ ...item, children: filteredChildren });
        }
      }

      return acc;
    }, []);
  };

  const filteredData = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return data;
    return hasDeepSearch ? filterDeep(data, term) : filterShallow(data, term);
  }, [data, searchText, hasDeepSearch]);

  // Auto-expand all items when deep searching so nested matches are visible
  const isSearching = hasDeepSearch && searchText.trim().length > 0;

  return (
    <View style={[styles?.container, { width, top: coords.y, left: coords.x, zIndex: 1400 }]}
    onLayout={(event) => {
      setRuntimeHeight(event.nativeEvent.layout.height)
    }}>
      {hasSearch && (
        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder={searchPlaceholder || "Search..."}
            value={searchText}
            onChangeText={setSearchText}
            placeholderTextColor="#7D7D7D"
          />
          <Feather name="search" size={16} color="#7D7D7D" style={styles.searchIcon} />
        </View>
      )}
      <ScrollView showsVerticalScrollIndicator={false}>
        {filteredData?.map((item, index) => {
          return !!item?.children && item?.children?.length > 0 ? (
            <View style={styles?.nested1stContainer} key={`${item?.id}-${index}`}>
              <TouchableOpacity
                disabled={item.isDisable}
                  style={[
                    styles?.nested1stTO,
                    {
                      borderTopStartRadius: index === 0 && !hasSearch ? 5 : 0,
                      borderTopEndRadius: index === 0 && !hasSearch ? 5 : 0,
                      borderBottomStartRadius: index === filteredData?.length - 1 ? 5 : 0,
                      borderBottomEndRadius: index === filteredData?.length - 1 ? 5 : 0,
                    },
                  ]}
                onPress={() => {
                  if (isSearching) return;
                  if (openedIndex === index) {
                    setOpenedIndex(-1);
                    return;
                  }
                  setOpenedIndex(index);
                }}
              >
                <NSText
                  style={{
                    maxWidth: "90%",
                    flexShrink: 1,
                    marginEnd: 5,
                    fontSize: 12,
                    color: item.isDisable ? "#BDBBBF" : "#303030",
                  }}
                  text={item?.name}
                />
                {item?.children?.length > 0 ? (
                  <AntDesign
                    name={(isSearching || openedIndex === index) ? "caret-up" : "caret-down"}
                    size={10}
                    color={item.isDisable ? "#BDBBBF" : "#303030"}
                  />
                ) : null}
              </TouchableOpacity>
              {(isSearching || openedIndex === index) ? (
                <ScrollView showsVerticalScrollIndicator={false}>
                  {item?.children?.map((val, idx) => {
                    return !!val?.children ? (
                      <View key={`${val?.id}-${idx}`}>
                        <TouchableOpacity
                          disabled={val.isDisable}
                          style={styles?.nested2ndTO}
                          onPress={() => {
                            if (isSearching) return;
                            if (openedSubIndex === idx) {
                              setOpenedSubIndex(-1);
                              return;
                            }
                            setOpenedSubIndex(idx);
                          }}
                        >
                          <NSText
                            style={[styles.textStyle, {color: val.isDisable ? "#BDBBBF" : "#303030"}]}
                            text={val?.name}
                          />
                          <AntDesign
                            name={
                              (isSearching || openedSubIndex === idx) ? "caret-up" : "caret-down"
                            }
                            size={10}
                            color={val.isDisable ? "#BDBBBF" : "#303030"}
                          />
                        </TouchableOpacity>
                        {(isSearching || openedSubIndex === idx) ? (
                          <ScrollView showsVerticalScrollIndicator={false}>
                            {val?.children?.map((v, i) => {
                              return (
                                <TouchableOpacity
                                  key={`opened-${v?.id}-${i}`}
                                  disabled={v.isDisable}
                                  style={[
                                    styles?.openedTO,
                                    {
                                      backgroundColor:
                                        selectedId === v?.id ? "#6300A35D" : "",
                                    },
                                  ]}
                                  onPress={() => {
                                    onItemPress(v, i);
                                    closeDropDown();
                                  }}
                                >
                                  <NSText
                                    style={{
                                      maxWidth: "90%",
                                      flexShrink: 1,
                                      fontSize: 12,
                                      color:
                                        selectedId === v?.id
                                          ? "white"
                                          : v.isDisable ? "#BDBBBF" : "#303030",
                                    }}
                                    text={v?.name}
                                  />
                                </TouchableOpacity>
                              );
                            })}
                          </ScrollView>
                        ) : null}
                      </View>
                    ) : (
                      <TouchableOpacity
                        disabled={val.isDisable}
                        style={[
                          styles?.openedTO2,
                          {
                            backgroundColor:
                              selectedId === val?.id ? "#6300A35D" : "",
                          },
                        ]}
                        onPress={() => {
                          onItemPress(val, idx);
                          closeDropDown();
                        }}
                      >
                        <NSText
                          style={{
                            maxWidth: "90%",
                            flexShrink: 1,
                            fontSize: 12,
                            color: selectedId === val?.id ? "white" : val.isDisable ? "#BDBBBF" : "#303030",
                          }}
                          text={val?.name}
                        />
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : null}
            </View>
          ) : (
            <TouchableOpacity
              key={`outside-${item?.id}-${index}`}
              disabled={item.isDisable}
              style={[
                styles?.outsideTO,
                {
                  backgroundColor: selectedId === item?.id ? "#6300A35D" : "",
                  borderTopStartRadius: index === 0 && !hasSearch ? 5 : 0,
                  borderTopEndRadius: index === 0 && !hasSearch ? 5 : 0,
                  borderBottomStartRadius: index === filteredData?.length - 1 ? 5 : 0,
                  borderBottomEndRadius: index === filteredData?.length - 1 ? 5 : 0,
                },
              ]}
              onPress={() => {
                onItemPress(item, index);
                closeDropDown();
              }}
            >
              <NSText
                style={{
                  maxWidth: "90%",
                  flexShrink: 1,
                  fontSize: 12,
                  color: selectedId === item?.id ? "white" : item.isDisable ? "#BDBBBF" : "#303030",
                }}
                text={item?.name}
              />
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
};

export default GlobalNestedPicker;

const styles = StyleSheet?.create?.({
  container: {
    position: "absolute",
    maxHeight: 300,
    borderRadius: 5,
    backgroundColor: "#F7F2FA",
    ...shadowStyle,
  },
  nested1stContainer: { width: "100%" },
  nested1stTO: {
    flexDirection: "row",
    padding: 10,
    backgroundColor: "",
    alignItems: "center",
    justifyContent: "space-between",
    elevation: 5,
  },
  nested1stText: {
    maxWidth: "90%",
    flexShrink: 1,
    marginEnd: 5,
    fontSize: 12,
    color: "#303030",
  },
  nested2ndTO: {
    flexDirection: "row",
    marginStart: 10,
    padding: 10,
    backgroundColor: "",
    alignItems: "center",
    justifyContent: "space-between",
    elevation: 5,
  },
  openedTO: { padding: 10, paddingStart: 30, elevation: 5 },
  openedText: { maxWidth: "90%", flexShrink: 1, fontSize: 12 },
  openedTO2: { padding: 10, paddingStart: 20, elevation: 5 },
  openedText2: { maxWidth: "90%", flexShrink: 1, fontSize: 12 },
  outsideTO: { padding: 10, elevation: 5 },
  outsideText: { maxWidth: "90%", flexShrink: 1, fontSize: 12 },
  textStyle:{
    maxWidth: "90%",
    flexShrink: 1,
    marginEnd: 5,
    fontSize: 12,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
    backgroundColor: "#F7F2FA",
    borderTopStartRadius: 5,
    borderTopEndRadius: 5,
  },
  searchInput: {
    width: "100%",
    fontSize: 14,
    color: "#303030",
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "white",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  searchIcon: {
    marginLeft: 8,
  },
});
